import { useState } from "react";
import { HoverTip } from "./HoverTip";
import type { TopologyDistance, TopologyHops, TopologySignal, TopologySnrColor } from "../api/types";
import { BACKBONE_PENDING } from '../lib/definitions/topology';
import { MESHCORE_SNR_COLOR_HEX } from '../lib/definitions/protocols';
import { chartStartIndex, fmtDb, historyFillingNote, localDayHour, localHour, NO_BASELINE, signalNoiseLevel, tzAbbrev } from "../lib/rfHealth";
import { formatDistanceBoth, type DistanceUnitPref } from "../lib/units";
import { useAppData } from "../state/AppData";
import { ResponsiveRecordTable } from "./ResponsiveRecordTable";

// Topology tab: the HOPS, DISTANCE and SIGNAL sections under the map. Every figure is
// precomputed per pass and read from the stored snapshot; this file only lays
// it out. Panels carry labels, numbers and short tags; each number has a
// one-line HoverTip. Distances come in km and are shown with the app's
// display-units setting: "122 mi (196 km)", or km first when the setting is km.

const DASH = "—";

function n0(v: number | null | undefined): string {
  return v === null || v === undefined ? DASH : v.toLocaleString("en-US");
}
function n1(v: number | null | undefined): string {
  return v === null || v === undefined ? DASH : v.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function NodeLink({ id, name }: { id: string; name: string }) {
  return (
    <a href={`#/nodes/${encodeURIComponent(id)}`} className="node-name-link">
      {name}
    </a>
  );
}

function Stat({ label, value, tip }: { label: string; value: string; tip: string }) {
  return (
    <HoverTip text={tip}>
      <span className="topo-stat">
        <span className="topo-stat-label">{label}</span> <span className="topo-stat-value">{value}</span>
      </span>
    </HoverTip>
  );
}

function Pending() {
  return <div className="node-detail-note">{BACKBONE_PENDING}</div>;
}

const HOPS_TIP = "Each packet counted once by the longest path any observer heard; paths over 25 hops are in the 26+ bar.";

export function HopsSection({ hops, window }: { hops: TopologyHops | undefined; window: string }) {
  if (!hops) {
    return (
      <>
        <Pending />
      </>
    );
  }
  const max = Math.max(1, ...hops.per_packet);
  return (
    <>
      <div className="topo-stats">
        <Stat label="Avg" value={n1(hops.avg)} tip={HOPS_TIP} />
        <Stat label="Median" value={n1(hops.median)} tip={HOPS_TIP} />
        <Stat label="Max" value={n0(hops.max)} tip={HOPS_TIP} />
        <Stat
          label="1 Hop"
          value={`${n0(hops.one_hop)} (${hops.one_hop_pct === null ? DASH : `${n0(Math.round(hops.one_hop_pct))}%`})`}
          tip="Packets whose longest heard path is one hop."
        />
      </div>

      <div className="topo-pair">
      <div>
      <h4 className="topo-sub-h">Hops Per Packet</h4>
      <div className="node-detail-note">packets, by the longest path any observer heard</div>
      <div className="topo-bars">
        {hops.per_packet.map((c, i) => (
          <div key={i} className="topo-bar-row">
            <span className="topo-bar-label">{i === 25 ? "26+" : i + 1}</span>
            <span className="topo-bar-track">
              <span className="topo-bar-fill" style={{ width: `${(c / max) * 100}%` }} />
            </span>
            <HoverTip text="Packets whose longest heard path has this many hops.">
              <span className="topo-bar-n">{n0(c)}</span>
            </HoverTip>
          </div>
        ))}
      </div>

      </div>
      <div>
      <h4 className="topo-sub-h">
        Top Repeaters In Paths
        <HoverTip text="Share and counts are for this window.">
          <span className="topo-tag">{window}</span>
        </HoverTip>
      </h4>
      <div className="node-detail-note">appearances are hop appearances across all receptions</div>
      <div className="node-list-table-wrap">
        <table className="node-list-table paths-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="rf-num">
                <HoverTip text="Times the repeater appears as a hop in a reception.">
                  <span>Appearances</span>
                </HoverTip>
              </th>
              <th className="rf-num">
                <HoverTip text="This repeater's appearances as a share of all hop appearances that resolved to one node in the window.">
                  <span>Share</span>
                </HoverTip>
              </th>
            </tr>
          </thead>
          <tbody>
            {hops.top_repeaters.map((r) => (
              <tr key={r.node_id}>
                <td>
                  <NodeLink id={r.node_id} name={r.name} />
                </td>
                <td className="rf-num">{n0(r.appearances)}</td>
                <td className="rf-num">{r.share_pct === null || r.share_pct === undefined ? DASH : `${n1(r.share_pct)}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
      </div>
      {hops.unresolved && (
        <div className="node-detail-note">
          {`Unresolved hop appearances left out (match no single node): ${n0(hops.unresolved.count)} (${hops.unresolved.pct === null ? DASH : `${n1(hops.unresolved.pct)}%`})`}
        </div>
      )}
    </>
  );
}

const LONGEST_LINKS_SHOWN = 10;
const OBSERVERS_SHOWN = 15;

const TYPE_LABEL: Record<string, string> = {
  repeater_repeater: "Repeater To Repeater",
  companion_repeater: "Companion To Repeater",
};

export function DistanceSection({ distance }: { distance: TopologyDistance | undefined }) {
  const { displaySettings } = useAppData();
  const pref: DistanceUnitPref | undefined = displaySettings?.display.distance_unit;
  const d = (v: number | null | undefined) => formatDistanceBoth(v, pref);
  const [showAllLinks, setShowAllLinks] = useState(false);
  if (!distance) {
    return (
      <>
        <Pending />
      </>
    );
  }
  const hist = distance.histogram;
  const hmax = Math.max(1, ...hist.bins);
  const doubtful = distance.excluded_doubtful ?? 0;
  const doubtfulNames = (distance.doubtful_positions ?? []).map((p) => p.name).join(", ");
  return (
    <>
      <div className="topo-stats">
        <Stat label="Repeater Links Analysed" value={n0(distance.links_analysed)} tip="Stable repeater-to-repeater links with both ends placed." />
        <Stat label="Avg" value={d(distance.avg_km)} tip="Mean length of those links." />
        <Stat label="Median" value={d(distance.median_km)} tip="Middle length of those links." />
        <Stat label="Longest" value={d(distance.longest_km)} tip="Longest of those links." />
        {distance.excluded_over_limit > 0 && (
          <Stat
            label={`Excluded Over ${d(distance.limit_km)}`}
            value={n0(distance.excluded_over_limit)}
            tip="Links longer than the limit are left out of every distance figure."
          />
        )}
        {doubtful > 0 && (
          <Stat
            label="Excluded: Doubtful Position"
            value={n0(doubtful)}
            tip={`Links touching a node whose position looks wrong (every link it has is over the limit, to neighbours that are otherwise well connected) are left out of every distance figure.${doubtfulNames ? ` Nodes: ${doubtfulNames}.` : ""}`}
          />
        )}
      </div>

      <div className="topo-pair">
      <div>
      <h4 className="topo-sub-h">By Link Type</h4>
      <div className="node-list-table-wrap">
        <table className="node-list-table paths-table">
          <thead>
            <tr>
              <th>Type</th>
              <th className="rf-num">Count</th>
              <th className="rf-num topo-col-low">Avg</th>
              <th className="rf-num topo-col-low">Median</th>
              <th className="rf-num">Max</th>
            </tr>
          </thead>
          <tbody>
            {distance.by_type.map((t) => (
              <tr key={t.type}>
                <td>{TYPE_LABEL[t.type] ?? t.type}</td>
                <td className="rf-num">{n0(t.count)}</td>
                <td className="rf-num topo-col-low">{d(t.avg_km)}</td>
                <td className="rf-num topo-col-low">{d(t.median_km)}</td>
                <td className="rf-num">{d(t.max_km)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      </div>
      <div>
      <h4 className="topo-sub-h">Link Distance</h4>
      {hist.min_km === null || hist.max_km === null ? (
        <div className="node-detail-note">{DASH}</div>
      ) : (
        <>
          <div className="topo-hist">
            {hist.bins.map((c, i) => {
              const width = (hist.max_km! - hist.min_km!) / hist.bins.length;
              const from = hist.min_km! + width * i;
              return (
                <HoverTip key={i} text={`${d(from)} to ${d(from + width)}: ${n0(c)} links`}>
                  <span className="topo-hist-col">
                    <span className="topo-hist-fill" style={{ height: c > 0 ? `max(2px, ${(c / hmax) * 100}%)` : 0 }} />
                  </span>
                </HoverTip>
              );
            })}
          </div>
          <div className="topo-hist-axis">
            <span>{d(hist.min_km)}</span>
            <span>{d(hist.max_km / 2)}</span>
            <span>{d(hist.max_km)}</span>
          </div>
        </>
      )}

      </div>
      </div>

      <h4 className="topo-sub-h">Longest Links</h4>
      <div className="node-list-table-wrap">
        <table className="node-list-table paths-table">
          <thead>
            <tr>
              <th>Link</th>
              <th className="rf-num">Distance</th>
              <th>
                <HoverTip text="Seen carrying traffic in both directions, or one.">
                  <span>Direction</span>
                </HoverTip>
              </th>
              <th className="rf-num topo-col-low">Days Seen</th>
              <th className="rf-num topo-col-low">Transmissions</th>
            </tr>
          </thead>
          <tbody>
            {(showAllLinks ? distance.longest_links : distance.longest_links.slice(0, LONGEST_LINKS_SHOWN)).map((l) => (
              <tr key={`${l.a}-${l.b}`}>
                <td>
                  <NodeLink id={l.a} name={l.a_name} /> <span className="paths-arrow">→</span> <NodeLink id={l.b} name={l.b_name} />
                </td>
                <td className="rf-num">{d(l.km)}</td>
                <td>{l.symmetry === "both_ways" ? "both ways" : l.symmetry === "one_way" ? "one way" : DASH}</td>
                <td className="rf-num topo-col-low">{n0(l.days_seen)}</td>
                <td className="rf-num topo-col-low">{n0(l.transmissions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!showAllLinks && distance.longest_links.length > LONGEST_LINKS_SHOWN && (
        <button type="button" className="topo-show-all" onClick={() => setShowAllLinks(true)}>
          {`Show Top ${n0(distance.longest_links.length)}`}
        </button>
      )}

      <h4 className="topo-sub-h">
        <HoverTip text="Longest paths any observer heard, every hop resolved to one node and each hop within the limit.">
          <span>Longest Paths</span>
        </HoverTip>
        {distance.longest_paths_window && (
          <HoverTip text="Longest paths come from the last 7 days in this window.">
            <span className="topo-tag">{distance.longest_paths_window}</span>
          </HoverTip>
        )}
      </h4>
      <div className="node-list-table-wrap">
        <table className="node-list-table paths-table">
          <thead>
            <tr>
              <th>Route</th>
              <th className="rf-num topo-col-low">Total Distance</th>
              <th className="rf-num topo-col-low">Hops</th>
            </tr>
          </thead>
          <tbody>
            {distance.longest_paths.map((p, i) => (
              <tr key={i}>
                <td className="paths-route">
                  <details className="topo-route-short">
                    <summary>
                      <NodeLink id={p.route_ids[0]} name={p.route[0]} /> <span className="paths-arrow">→</span> <NodeLink id={p.route_ids[p.route.length - 1]} name={p.route[p.route.length - 1]} />
                      <span className="topo-route-hops">{` ${n0(p.hops)} hops`}</span>
                      <span className="topo-route-meta">{` ${n0(p.hops)} hops, ${d(p.total_km)}`}</span>
                    </summary>
                    <div className="topo-route-list">
                      {p.route.map((name, j) => (
                        <span key={j}>
                          {j > 0 && <span className="paths-arrow"> → </span>}
                          <NodeLink id={p.route_ids[j]} name={name} />
                        </span>
                      ))}
                    </div>
                  </details>
                </td>
                <td className="rf-num topo-col-low">{d(p.total_km)}</td>
                <td className="rf-num topo-col-low">{n0(p.hops)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// --- SIGNAL -------------------------------------------------------------------
// Signal exists only where a packet is finally heard: at an observer, for the
// last hop that handed it over. MeshCore packets carry no per-hop signal, so
// every figure here is observer-to-transmitter.

const SNR_TREND_FLAT_DB = 0.5;

function SnrCell({ db, color }: { db: number | null | undefined; color?: TopologySnrColor }) {
  return (
    <span className="snr-cell">
      <span
        className="node-neighbors-meshcore-swatch"
        style={color ? { background: MESHCORE_SNR_COLOR_HEX[color] } : { visibility: "hidden" }}
      />
      <span className="snr-num">{db === null || db === undefined ? DASH : `${fmtDb(db)} dB`}</span>
    </span>
  );
}

function SnrTrend({ db }: { db: number | null }) {
  if (db === null) {
    return (
      <HoverTip text="The previous equal window has fewer than 10 samples for this link.">
        <span className="rf-trend">{NO_BASELINE}</span>
      </HoverTip>
    );
  }
  const tip = "Average SNR in this window minus the same link's average in the previous equal window.";
  if (Math.abs(db) < SNR_TREND_FLAT_DB) {
    return (
      <HoverTip text={tip}>
        <span className="rf-trend" data-tone="flat">{`▬ flat (${db > 0 ? "+" : ""}${fmtDb(db)} dB)`}</span>
      </HoverTip>
    );
  }
  return (
    <HoverTip text={tip}>
      <span className="rf-trend" data-tone={db > 0 ? "better" : "worse"}>{`${db > 0 ? "▲" : "▼"} ${fmtDb(Math.abs(db))} dB`}</span>
    </HoverTip>
  );
}

// "SNR Over Time · line: hourly median of link averages (1.3 to 3.7 dB)": the line is the median, across heard links,
// of each link's average SNR in the slot (topology.py _signal_block), not a median over every sample.
function snrOverTimeTitle(points: TopologySignal["over_time"], bucketHours: number): string {
  const start = chartStartIndex(points.map((p) => p.samples));
  const meds = points.slice(start).map((p) => p.median_db).filter((v): v is number => v !== null);
  const range = meds.length ? ` (${fmtDb(Math.min(...meds))} to ${fmtDb(Math.max(...meds))} dB)` : "";
  return `SNR Over Time \u00b7 line: ${bucketHours >= 24 ? "daily" : "hourly"} median of link averages${range}`;
}

function SignalOverTime({ points: allPoints, bucketHours, windowKey }: { points: TopologySignal["over_time"]; bucketHours: number; windowKey: string }) {
  if (allPoints.length === 0) return <div className="node-detail-note">{DASH}</div>;
  // The chart starts at the first slot with data; the note below says when the history began.
  const start = chartStartIndex(allPoints.map((p) => p.samples));
  const points = allPoints.slice(start);
  const label = bucketHours >= 24 ? localDayHour : windowKey === "24h" || windowKey === "12h" || windowKey === "6h" || windowKey === "1h" ? localHour : localDayHour;
  const tz = tzAbbrev(points[0].hour_start);
  const maxN = Math.max(1, ...points.map((p) => p.samples));
  const meds = points.map((p) => p.median_db).filter((v): v is number => v !== null);
  const lo = meds.length ? Math.min(...meds) : 0;
  const hi = meds.length ? Math.max(...meds) : 1;
  const span = hi - lo || 1;
  const yPct = (v: number) => 100 - (8 + ((v - lo) / span) * 84);
  const fillNote = historyFillingNote(allPoints.map((p) => p.samples), (i) => `${label(allPoints[i].hour_start)} ${tz}`);
  const step = 100 / points.length;
  const pts = points.map((p, i) => (p.median_db === null ? null : { x: step * (i + 0.5), y: yPct(p.median_db) }));
  const line = pts.filter((p): p is { x: number; y: number } => p !== null).map((p) => `${p.x},${p.y}`).join(" ");
  return (
    <>
      <div className="topo-sig-chart">
        <div className="topo-sig-cols">
          {points.map((p) => (
            <HoverTip
              key={p.hour_start}
              text={`${label(p.hour_start)} ${tz}: ${p.median_db === null ? "no median" : `median ${fmtDb(p.median_db)} dB`}, ${n0(p.samples)} samples`}
            >
              <span className={`topo-sig-col${p.samples === 0 ? " topo-sig-col-empty" : ""}`}>
                <span className="topo-sig-bar" style={{ height: `${(p.samples / maxN) * 100}%` }} />
              </span>
            </HoverTip>
          ))}
        </div>
        <svg className="topo-sig-line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <polyline points={line} fill="none" stroke="var(--ink)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
      <div className="topo-hist-axis topo-sig-axis">
        <span>{`${label(points[0].hour_start)} ${tz}`}</span>
        <span>{`${label(points[points.length - 1].hour_start)} ${tz}`}</span>
      </div>
      {fillNote && <div className="node-detail-note">{fillNote}</div>}
    </>
  );
}

export function SignalSection({ signal, windowKey }: { signal: TopologySignal | undefined; windowKey: string }) {
  const [showAllObs, setShowAllObs] = useState(false);
  if (!signal) {
    return (
      <>
        <Pending />
      </>
    );
  }
  const minN = signal.min_samples ?? 10;
  // Older stored snapshots predate this policy field; keep their badge behavior stable.
  const bestAvgSpreadFlagDb = signal.best_avg_spread_db ?? 15;
  const asymDb = signal.asymmetry_db ?? 6;
  const bz = signal.below_zero_pct === null ? DASH : `${n0(Math.round(signal.below_zero_pct))}%`;
  const bucketHours = signal.over_time_bucket_hours ?? 1;
  const anyTrend = signal.weakest.some((w) => w.trend_db !== null && w.trend_db !== undefined);
  const showTrend = signal.weakest.length === 0 || anyTrend;
  const noTrend = signal.weakest.length > 0 && !anyTrend;
  return (
    <>
      <div className="topo-stats">
        <Stat label="Heard Links" value={n0(signal.heard_links)} tip={`Observer and transmitter pairs with at least ${minN} receptions with SNR in the window. The transmitter is the last hop that handed the packet to the observer.`} />
        <Stat label="Median Link SNR" value={signal.median_snr_db === null ? DASH : `${fmtDb(signal.median_snr_db)} dB`} tip="Middle of the average SNR of every heard link: each link is averaged first, then the median is taken across links." />
        <Stat label="Below 0 dB" value={`${n0(signal.below_zero)} (${bz})`} tip="Heard links whose average SNR is under 0 dB." />
        <Stat label="Observers" value={n0(signal.observers)} tip="Observers with at least one heard link." />
      </div>

      <h4 className="topo-sub-h">
        {snrOverTimeTitle(signal.over_time, bucketHours)}
        <HoverTip text={`${bucketHours >= 24 ? "Daily" : "Hourly"} median across heard links of each link's average SNR in that slot (line), with the number of samples as bars. A link counts in a slot with at least 3 samples. Slots with no samples are hatched.`}>
          <span className="topo-tag">bars are samples</span>
        </HoverTip>
      </h4>
      <SignalOverTime points={signal.over_time} bucketHours={bucketHours} windowKey={windowKey} />

      <h4 className="topo-sub-h">
        Weakest Heard Links (top 20)
        <HoverTip text="Signal is measured only where a packet is finally heard, so each figure is observer to transmitter, never per hop.">
          <span className="topo-tag">observer to transmitter</span>
        </HoverTip>
        {noTrend && <span className="topo-tag">Trend needs a previous period</span>}
      </h4>
      <ResponsiveRecordTable
        tableClassName="paths-table"
        ariaLabel="Weakest heard links"
        columns={[
          { key: "link", header: <HoverTip text="The observer that heard the packet, and the last hop that handed it over."><span>Observer ← Transmitter</span></HoverTip>, mobileLabel: "Observer ← Transmitter" },
          { key: "snr", header: <HoverTip text="Average SNR of the link in the window, with the best single reading in brackets."><span>Avg SNR (best)</span></HoverTip>, mobileLabel: "Avg SNR (best)", className: "rf-num" },
          { key: "samples", header: <HoverTip text="Receptions with an SNR value in the window."><span>Samples</span></HoverTip>, mobileLabel: "Samples", className: "rf-num topo-col-low" },
          ...(showTrend ? [{ key: "trend", header: <HoverTip text="Change in the link's average against the previous equal window."><span>Trend</span></HoverTip>, mobileLabel: "Trend", className: "topo-col-low" }] : []),
        ]}
        rows={signal.weakest.map((w) => ({
          key: `${w.observer_id}-${w.transmitter_id}`,
          cells: {
            link: <><NodeLink id={w.observer_id} name={w.observer_name} /> <span className="paths-arrow">←</span>{" "}<NodeLink id={w.transmitter_id} name={w.transmitter_name} /></>,
            snr: <><SnrCell db={w.avg_db} color={w.avg_color} /> <span className="node-neighbors-meshcore-snr-best snr-best">{`(${fmtDb(w.best_db)})`}</span>{w.best_db - w.avg_db >= bestAvgSpreadFlagDb && <span className="topo-tag" title={`Best single reception minus this same link's average SNR across all receptions in the selected window. This marks a large spread at the ${fmtDb(bestAvgSpreadFlagDb)} dB operator policy; it does not identify its cause.`}>{`Best–avg +${fmtDb(w.best_db - w.avg_db)} dB`}</span>}</>,
            samples: n0(w.samples),
            ...(showTrend ? { trend: <SnrTrend db={w.trend_db} /> } : {}),
          },
        }))}
      />

      <h4 className="topo-sub-h">
        Asymmetric Pairs
        <HoverTip text={`Two nodes that each heard the other, at least ${minN} samples both ways, whose average SNR differs by ${fmtDb(asymDb)} dB or more.`}>
          <span className="topo-tag">{`${fmtDb(asymDb)} dB or more`}</span>
        </HoverTip>
      </h4>
      {signal.asymmetric.length === 0 ? (
        <div className="node-detail-note">{DASH}</div>
      ) : (
        <ResponsiveRecordTable
          tableClassName="paths-table"
          ariaLabel="Asymmetric signal pairs"
          columns={[
            { key: "ab", header: <HoverTip text="Average SNR at the first node for packets last transmitted by the second."><span>A Hears B</span></HoverTip>, mobileLabel: "A Hears B" },
            { key: "ba", header: <HoverTip text="Average SNR at the second node for packets last transmitted by the first."><span>B Hears A</span></HoverTip>, mobileLabel: "B Hears A" },
            { key: "diff", header: <HoverTip text="The gap between the two averages."><span>Difference</span></HoverTip>, mobileLabel: "Difference", className: "rf-num" },
            { key: "samples", header: <HoverTip text="Samples behind each direction, A hears B first."><span>Samples</span></HoverTip>, mobileLabel: "Samples (A hears B / B hears A)", className: "rf-num" },
          ]}
          rows={signal.asymmetric.map((a) => ({
            key: `${a.a_id}-${a.b_id}`,
            cells: {
              ab: <><span className="topo-asym-line"><NodeLink id={a.a_id} name={a.a_name} /> </span><span className="topo-asym-line"><span className="paths-arrow">←</span> <NodeLink id={a.b_id} name={a.b_name} /><span className="node-neighbors-meshcore-snr-best">{` ${fmtDb(a.ab_db)} dB`}</span></span></>,
              ba: <><span className="topo-asym-line"><NodeLink id={a.b_id} name={a.b_name} /> </span><span className="topo-asym-line"><span className="paths-arrow">←</span> <NodeLink id={a.a_id} name={a.a_name} /><span className="node-neighbors-meshcore-snr-best">{` ${fmtDb(a.ba_db)} dB`}</span></span></>,
              diff: `${fmtDb(a.diff_db)} dB`,
              samples: `${n0(a.ab_samples)} / ${n0(a.ba_samples)}`,
            },
          }))}
        />
      )}

      <h4 className="topo-sub-h">Observer SNR</h4>
      <ResponsiveRecordTable
        tableClassName="paths-table"
        ariaLabel="Observer signal summary"
        columns={[
          { key: "observer", header: "Observer", mobileLabel: "Observer" },
          { key: "links", header: <HoverTip text="Heard links this observer has in the window."><span>Links Heard</span></HoverTip>, mobileLabel: "Links Heard", className: "rf-num" },
          { key: "median", header: <HoverTip text="Middle of the average SNR of this observer's heard links. Worst first."><span>Median SNR</span></HoverTip>, mobileLabel: "Median SNR", className: "rf-num" },
          { key: "noise", header: <HoverTip text="Middle of the observer's own noise floor readings in the window."><span>Noise Floor</span></HoverTip>, mobileLabel: "Noise Floor", className: "rf-num topo-col-low" },
          { key: "below", header: <HoverTip text="This observer's heard links with an average SNR under 0 dB, and their share."><span>Below 0 dB</span></HoverTip>, mobileLabel: "Below 0 dB", className: "rf-num topo-col-low" },
        ]}
        rows={(showAllObs ? signal.observer_margin : signal.observer_margin.slice(0, OBSERVERS_SHOWN)).map((m) => ({
          key: m.observer_id,
          cells: {
            observer: <NodeLink id={m.observer_id} name={m.name} />,
            links: n0(m.links),
            median: <SnrCell db={m.median_db} color={m.median_color} />,
            noise: m.noise_floor_dbm === null ? DASH : <span className="rf-level" data-level={signalNoiseLevel(m.noise_floor_dbm) ?? "none"}>{`${fmtDb(m.noise_floor_dbm)} dBm`}</span>,
            below: `${n0(m.below_zero)} (${n0(Math.round(m.below_zero_pct))}%)`,
          },
        }))}
      />
      {signal.observer_margin.length > OBSERVERS_SHOWN && (
        <button type="button" className="topo-show-all" onClick={() => setShowAllObs((v) => !v)}>
          {showAllObs ? "Show Fewer" : `Show All ${n0(signal.observer_margin.length)} Observers`}
        </button>
      )}
    </>
  );
}
